import {LoaderFunctionArgs} from 'react-router-dom';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {IRouteStore} from '../../router/routing.infra';
import {RouteName} from '../../router/route-names';
import {RolesConstant} from '@techsee/techsee-common/lib/constants/account.constants';
import {TsEnvironmentDetect} from '@techsee/techsee-common/lib/helpers/ts-environment-detect';
import {Flavors} from '../../services/ts-ui-flavor/ts-ui-flavor.service';
import {buildLoginRedirectParamsFromUrl} from '../../services/auth/auth-handler.service';
import {MULTI_USER_LOGIN_CONTEXT_KEY} from '../login/login.loader';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
const trace = getAppTracer('invite.loader');

const cleanParams = (params: Record<string, any>) => getRootStore().browserUtilsService.cleanParams(params);

const navigateToLoginWithPreservedParams = async (nav: any, url: URL): Promise<void> => {
    await nav.navigateTo(RouteName.LOGIN, buildLoginRedirectParamsFromUrl(url));
};

// Helper to determine account flavor based on device and settings
const getAccountFlavor = (accountData: any, isPhone: boolean, isTablet: boolean): Flavors => {
    if (isPhone && !isTablet) {
        return Flavors.Default;
    }
    return get(accountData, 'protectedSettings.enableNewInvite') ? Flavors.NewInvite : Flavors.Default;
};

// PHASE 1: Session cleanup
function cleanupSessionStorage(rootStore: any): void {
    rootStore.browserUtilsService.saveToSessionStorage('screenshots', []);
    rootStore.browserUtilsService.removeFromSessionStorage('socketConfig');
    rootStore.browserUtilsService.removeFromSessionStorage('usingApplication');
    rootStore.browserUtilsService.removeFromSessionStorage('sessionStartedTimestamp');
    rootStore.browserUtilsService.removeFromSessionStorage('shouldNotDisplayAgentPerformance');
}

// PHASE 1: Get and validate current user with OTT, browser support, password check
async function getCurrentUserWithValidation(rootStore: any, url: URL): Promise<any> {
    const nav = rootStore.tsReactStateHelper;

    // 2a. Handle OTT (One Time Token) login
    const ott = url.searchParams.get('ott');
    if (ott) {
        try {
            const redeemResult = await rootStore.authService.loginWithOneTimeToken(ott);
            if (redeemResult?.isRedeemed) {
                rootStore.browserUtilsService.setParamValue('ott', null);
            }
        } catch (error) {
            trace.error('OTT login failed', {error});
            // Continue anyway to try getting current user
        }
    }

    // 2b. Get current user
    const currentUser = await rootStore.userService.getUser('current');

    if (!currentUser) {
        trace.warn('No current user found, redirecting to login');
        await navigateToLoginWithPreservedParams(nav, url);
    }

    // 2c. Account selection / missing account guard
    if (!currentUser?.accountId) {
        const hasAccounts = Array.isArray(currentUser?.accounts) && !isEmpty(currentUser.accounts);
        if (hasAccounts) {
            const multiUserLoginContext = {
                accountId: currentUser.accountId,
                accounts: currentUser.accounts
            };

            rootStore.browserUtilsService.saveToSessionStorage(MULTI_USER_LOGIN_CONTEXT_KEY, multiUserLoginContext);
            await nav.navigateTo(RouteName.LOGIN);
            return currentUser;
        }

        await navigateToLoginWithPreservedParams(nav, url);
        return currentUser;
    }

    // 2d. Browser support check (unless sysadmin/superadmin)
    const isSysadminOrSuperAdmin =
        currentUser.role === RolesConstant.SYSADMIN || currentUser.role === RolesConstant.SUPERADMIN;

    if (!isSysadminOrSuperAdmin) {
        try {
            const browserClientHints = await TsEnvironmentDetect.getBrowserClientHints();
            const clientHints = btoa(JSON.stringify(browserClientHints));

            const browserSupport = await rootStore.generalApiService.isSupportedByAccount({
                accountId: currentUser.accountId,
                ua: window.navigator.userAgent,
                clientHints
            });

            trace.info('Browser support check result', {browserSupport, isEmpty: isEmpty(browserSupport)});

            if (!isEmpty(browserSupport)) {
                trace.info('Navigating to browser-not-supported', {
                    browserInfo: browserSupport,
                    userName: currentUser.firstName
                });
                rootStore.appState.browserNotSupportedState.browserInfo = browserSupport;
                rootStore.appState.browserNotSupportedState.userName = currentUser.firstName || '';
                await nav.navigateTo(RouteName.BROWSER_NOT_SUPPORTED);
            }
        } catch (error) {
            if (error instanceof Response) throw error;
            trace.warn('Browser support check failed, continuing anyway', {error});
        }
    }

    // 2e. Password change check
    const ssoAuthId = url.searchParams.get('authId');
    const guestObserver = currentUser.role === RolesConstant.GUEST_OBSERVER;
    const isMultiAccountUser = Boolean(currentUser.parentSubUserId);

    if (currentUser.requirePasswordChange && !ssoAuthId && !guestObserver && !isMultiAccountUser) {
        await nav.navigateTo(RouteName.CHANGE_PASSWORD);
    }

    return currentUser;
}

// PHASE 1: Validate guest observer access — throws redirect if invalid
async function validateGuestObserver(currentUser: any, url: URL): Promise<void> {
    if (currentUser.role === RolesConstant.GUEST_OBSERVER) {
        const roomId = url.searchParams.get('roomId');
        const g = url.searchParams.get('g');
        const roomCode = url.searchParams.get('roomCode');
        const observe = url.searchParams.get('observe');

        if (!(roomId || g || roomCode) || !observe) {
            const rootStore = getRootStore();

            rootStore.authService.clearAuthToken();
            await rootStore.tsReactStateHelper.navigateTo(RouteName.INVITATION_ENDED);
        }
    }
}

// PHASE 2: Get and process account data
async function getAndProcessAccountData(
    rootStore: any,
    currentUser: any,
    url: URL,
    displayTabletAsDesktop: boolean
): Promise<any> {
    const nav = rootStore.tsReactStateHelper;
    const accountData = await rootStore.accountService.getAccount(currentUser.accountId);

    // 5a. Set UI Flavor based on device and account settings
    const isTablet = rootStore.environmentService.isTablet(displayTabletAsDesktop) || url.searchParams.get('tablet');
    const isPhone = rootStore.environmentService.isPhone();

    const flavor = getAccountFlavor(accountData, isPhone, isTablet);
    rootStore.uiFlavorService.setFlavor(flavor);

    // 5b. Knowledge Expert restrictions
    if (
        currentUser.role === RolesConstant.KNOWLEDGE_EXPERT &&
        !accountData.protectedSettings.knowledgeExpertRunSessions
    ) {
        await nav.navigateTo(RouteName.DASHBOARD_MAIN, cleanParams({offline: true}));
    }

    // 5c. Handle tablet as desktop preference
    if (isTablet && !displayTabletAsDesktop) {
        rootStore.browserUtilsService.saveToLocalStorage('displayTabletAsDesktop', true);
        window.location.reload();
        // This will reload the page and re-enter the loader
        await nav.navigateTo(RouteName.INVITE);
    }

    // 5d. Set interface type settings
    rootStore.tsInterfaceHelperService.setInterfaceType(accountData.protectedSettings.enableNewDashboardUI);
    rootStore.tsInterfaceHelperService.setHeaderFooterLeftbarType(true);

    // 5e. Fetch VJ flows if using Flow Management V2
    if (accountData?.protectedSettings?.visualJourney?.useFlowManagementV2) {
        try {
            const flowsResponse = await rootStore.generalApiService.getFlows({
                accountId: currentUser.accountId,
                statuses: 'production'
            });

            accountData.protectedSettings.visualJourney.available =
                flowsResponse?.flows?.map((flow: any) => ({
                    name: flow?.metaData[0]?.name,
                    alias: flow?.urlAlias
                })) || [];
        } catch (error) {
            // Continue with empty flows on error
            trace.warn('Failed to fetch VJ flows', {error});
        }
    }

    return accountData;
}

// PHASE 3: Initialize PostMessage API (jsApiService)
function initializeJsApiService(rootStore: any, accountData: any, url: URL): void {
    const exposePostMessageApi = accountData?.protectedSettings?.exposePostMessageApi;
    if (exposePostMessageApi?.enabled) {
        const accountSettingsJsApi = {
            timezone: accountData?.settings?.timezone,
            language: accountData?.settings?.language
        };

        rootStore.jsApiService.init(null, {
            ...exposePostMessageApi,
            ...accountSettingsJsApi
        });

        // Handle SSO login callbacks
        const loginCallbackResult = rootStore.browserUtilsService.getFromLocalStorage('loginCallbackResult');
        const authId =
            url.searchParams.get('authId') || rootStore.browserUtilsService.getFromLocalStorage('auth')?.authId;

        if (loginCallbackResult) {
            rootStore.jsApiService.SSOLoginSuccess(authId);
        } else if (loginCallbackResult === false) {
            rootStore.jsApiService.SSOLoginFailure(authId);
        }

        // Handle OTT login
        const ott = url.searchParams.get('ott');
        if (ott) {
            rootStore.jsApiService.OTTLoginSuccess();
        }
    }
}

// PHASE 4: Check for URL-based redirects (warm transfer, VJ history, join room)
// Throws redirect if navigation needed, otherwise returns normally
async function checkUrlRedirects(rootStore: any, url: URL): Promise<void> {
    const nav = rootStore.tsReactStateHelper;
    const urlParams = {
        prg: url.searchParams.get('prg'),
        is: url.searchParams.get('is'),
        roomId: url.searchParams.get('roomId'),
        g: url.searchParams.get('g'),
        roomCode: url.searchParams.get('roomCode'),
        observe: url.searchParams.get('observe'),
        emb: url.searchParams.get('emb'),
        mini: url.searchParams.get('mini'),
        sw: url.searchParams.get('sw'),
        audio: url.searchParams.get('audio'),
        r: url.searchParams.get('r'),
        startDate: url.searchParams.get('startDate'),
        endDate: url.searchParams.get('endDate')
    };

    // Handle warm transfer redirect
    if (urlParams.prg) {
        trace.info('Handling warm transfer request');
        try {
            const result = await rootStore.generalApiService.activatePendingRoom({
                pendingRoomGuid: urlParams.prg
            });

            await nav.navigateTo(
                RouteName.JOIN,
                cleanParams({
                    roomId: urlParams.roomId || result?.roomId,
                    shortId: urlParams.g || result?.g,
                    roomCode: urlParams.roomCode,
                    audio: urlParams.audio,
                    observe: urlParams.observe,
                    emb: urlParams.emb,
                    mini: urlParams.mini,
                    sw: urlParams.sw,
                    prg: urlParams.prg
                })
            );
        } catch (error) {
            if (error instanceof Response) throw error;
            trace.error('Warm transfer activation failed', {error});
            await nav.navigateTo(
                RouteName.INVITATION_ENDED,
                cleanParams({
                    err: (error as any)?.response?.status,
                    sessionType: 'warmTransfer'
                })
            );
        }
    }

    // Handle VJ history redirect
    if (urlParams.is?.toLowerCase() === 'vjhistory') {
        trace.info('Redirecting to VJ history');
        await nav.navigateTo(
            RouteName.VJ_HISTORY,
            cleanParams({
                emb: urlParams.emb,
                r: urlParams.r,
                startDate: urlParams.startDate,
                endDate: urlParams.endDate,
                is: urlParams.is,
                roomId: urlParams.roomId
            })
        );
    }

    // Handle join room redirect
    if (urlParams.roomId || urlParams.g || urlParams.roomCode) {
        trace.info('Redirecting to join room');
        await nav.navigateTo(
            RouteName.JOIN,
            cleanParams({
                roomId: urlParams.roomId,
                shortId: urlParams.g,
                roomCode: urlParams.roomCode,
                audio: urlParams.audio,
                observe: urlParams.observe,
                emb: urlParams.emb,
                mini: urlParams.mini,
                sw: urlParams.sw
            })
        );
    }
}

export async function inviteRouteLoader(args: LoaderFunctionArgs): Promise<IRouteStore> {
    trace.info('Starting');

    try {
        const rootStore = getRootStore();
        const url = new URL(args.request.url);

        // PHASE 1: Security & Session Management
        // 1. Session cleanup
        cleanupSessionStorage(rootStore);

        // 2. Get & validate current user (with OTT, multi-user, browser support, password check)
        // Throws redirect if validation fails
        const currentUser = await getCurrentUserWithValidation(rootStore, url);

        // 3. Guest observer validation — throws redirect if invalid
        await validateGuestObserver(currentUser, url);

        // PHASE 2: Account Data Processing — throws redirect if needed
        const displayTabletAsDesktop = rootStore.displayTabletAsDesktop;
        const accountData = await getAndProcessAccountData(rootStore, currentUser, url, displayTabletAsDesktop);

        // PHASE 3: Service Initialization
        // Initialize extra logging for this account
        rootStore.tsExtraLoggingService.init(accountData, rootStore.eventLogService);

        // Initialize Matomo Analytics
        const enableMatomoAnalytics = accountData?.protectedSettings?.enableMatomoAnalytics;
        if (enableMatomoAnalytics) {
            // Note: Matomo script loading requires $rootScope.loadScript from Angular
            // In pure React context, we'd need to implement dynamic script loading
            // For now, log that it's enabled (will be handled when full migration is complete)
            trace.info('Matomo analytics enabled but script loading deferred to post-migration');
        }

        // Initialize PostMessage API (jsApiService)
        initializeJsApiService(rootStore, accountData, url);

        // PHASE 4: URL-based Redirects — throws redirect if navigation needed
        await checkUrlRedirects(rootStore, url);

        // PHASE 5: Store Creation
        // Device detection
        const isMobile = rootStore.environmentService.isMobile(displayTabletAsDesktop);
        trace.info('Device detection', {isMobile});

        // Join account channel — must be awaited so pendingRooms/wtPendingRooms are populated
        // before OfflineSessionsController and PendingRoomSessionsController read them in initStores()
        try {
            await rootStore.accountSocketService.joinAccountChannel();
        } catch (err) {
            trace.error('Failed to join account channel', {error: err});
        }

        // Dynamically import and create the appropriate store
        let store: IRouteStore;

        if (isMobile) {
            trace.info('Creating mobile store');
            const {InviteMobileStores} = await import('../invite-mobile/invite-mobile.stores');
            store = new InviteMobileStores(accountData as any, currentUser as any);
        } else {
            trace.info('Creating desktop store');
            const {InviteStores} = await import('../invite-new/invite.stores');
            const desktopStore = new InviteStores(accountData, currentUser, rootStore.tsReactStateHelper);
            await desktopStore.initStores();
            store = desktopStore;
        }

        // PHASE 6: Invite Module Load Logging
        const roomId = url.searchParams.get('roomId');
        const g = url.searchParams.get('g');
        const roomCode = url.searchParams.get('roomCode');
        const room = roomId || g || roomCode;

        // Log with timeout (similar to PromiseUtilsService.startPromiseWithTimeout)
        setTimeout(() => {
            trace.info(`invite module has loaded! account: ${accountData._id.toString()} - room: ${room}`);
        }, 3000);

        trace.info('Completed successfully', {isMobile});
        return store;
    } catch (error) {
        if (error instanceof Response) throw error;
        trace.error('Failed to load invite route', {error});
        throw error;
    }
}
